<template>
  <section class="entry-form-view entry-view">

    <div class="">
      <div class="entry-view__logo">
        <img class="entry-view__logo__img" :src="redBullLogo" alt="Red Bull Logo">
      </div>
      <h1 class="entry-form-view__headline">{{ copyHeadline }}</h1>
      <div class="entry-view__content-wrapper">
        <div class="entry-form-view__preview-area">
          <terms-display class="entry-form-view__terms-display" v-if="showTerms" />

          <div class="entry-form-view__preview-area__frame" v-show="showFrame">
            <!-- Static states: error, upload, and flagged -->
            <entry-form-message
              v-if="useMessage"
              :messageType="messageType"
              @click="handleMessageClick()"
              :overrideButtonText="quicktimeMessage ? copySubmit : null"
              ></entry-form-message>
            <file-upload
              v-show="mode === 'upload'"
              class="entry-form-view_file-upload"
              @error="handleError"
              @change="handleUploadChange"
            ></file-upload>
          </div>
          <!-- Image Cropper: crop mode -->
          <div class="entry-form-view__preview-area__image-cropper-wrap"
            :class="{
              'entry-form-view__preview-area__image-cropper-wrap--visible': cropperReady
            }"
            v-show="showCropper">
            <div class="entry-form-view__preview-area__flex-wrap">
              <image-cropper
                v-if="useCropper"
                ref="imageCropper"
                class="entry-form-view__image-cropper"
                @ready="handleCropperReady"
                @mounted="submissionBusy = false"
                :src="cropSrc" />
            </div>
            <div class="entry-form-view__thumbnails" v-if="useVideo && !hideVideoSubmission">
              <div class="entry-form-view__thumbnails-label">{{copyVideoThumbnails}}</div>
              <entry-form-thumbnails
                v-if="useVideo"
                :src="entrySrc"

                ref="videoThumbnail"
                @thumbnailExport="handleExportThumbnails"
                @error="handleThumbnailError"
                @click="setCropSrc" />
            </div>
          </div>
          <!-- Loader -->
          <transition name="fade" appear>
            <div class="entry-form-view__preview-area__loader" v-if="showLoader">
              <entry-loader>
              </entry-loader>
            </div>
          </transition>
        </div>
        <div class="entry-form-view__instructions">
          <p class="entry-form-view__instructions--callout" v-if="!showCropper && !showTerms && !quicktimeMessage">{{copyCallout}}</p>
          <p class="entry-form-view__instructions--copy" v-if="!showCropper && !showTerms && !quicktimeMessage">{{copyInstructions}}</p>
          <p class="entry-form-view__instructions-line" v-if="showCropper && !showTerms && !quicktimeMessage">{{copyInstructionsCrop}}</p>
          <p class="entry-form-view__instructions-line" v-if="showCropper || quicktimeMessage">
            <a class="entry-view__link" href="#" tabIndex="1" v-if="entryFile" @click.prevent="handleReset">{{copyResetCta}}</a>
          </p>

          <label class="entry-form-view__instructions__label entry-form-view__instructions--terms">
            <input type="checkbox" v-model="terms">
            {{copyTerms}}
            <a
              class="entry-view__link"
              href="#"
              tabIndex="1"
              @click.prevent="handleTermsClick"
            >{{copyTermsCta}}</a>.
          </label>
        </div>
        <div class="entry-form-view__controls">
          <button
            class="entry-view__main-cta entry-form-view__submit"
            :disabled="!canSubmit && !showTerms"
            @click="handleSubmitClick"
            tabindex="1"
          >{{copySubmit}}</button>
        </div>
        </div>
      </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { SUBMISSION_MAKE_SUBMISSION } from './../core/store/action-types';
import {
  errors,
  ROUTE_NAMES,
  SUBMISSION_CATEGORIES
} from './../core/config';
import { devError } from './../core/utils';

import CheckRef from './../mixins/check-ref';
import ImageCropper from './../components/image-cropper';
import FileUpload from './../components/file-upload';
import EntryFormThumbnails from './../components/entry-form-thumbnails';
import EntryFormMessage from './../components/entry-form-message';
import EntryLoader from './../components/entry-loader';
import TermsDisplay from './../components/terms-display';
import redBullLogo from './../assets/images/red-bull-logo.png';

import {
  CONTENT_ENTRY_FORM_INSTRUCTIONS_CALLOUT,
  CONTENT_ENTRY_FORM_INSTRUCTIONS_COPY,
  CONTENT_ENTRY_FORM_CONTINUE_CTA,
  CONTENT_ENTRY_FORM_CONSENT_NAME_COPY,
  CONTENT_ENTRY_FORM_SUBMIT_CTA,
  CONTENT_ENTRY_FORM_TERMS_CTA,
  CONTENT_ENTRY_FORM_TERMS_COPY,
  CONTENT_ENTRY_FORM_RESET_CTA,
  CONTENT_ENTRY_FORM_THUMBNAILS_COPY,
  CONTENT_ENTRY_FORM_INSTRUCTIONS_CROP,
  CONTENT_ENTRY_FORM_CLOSE_TERMS_CTA,
  CONTENT_GENERAL_HEADLINE
} from '../../design-tokens/build/dictionary';

export default {
  name: 'entry-form-view',
  components: {
    ImageCropper,
    FileUpload,
    EntryFormMessage,
    EntryFormThumbnails,
    TermsDisplay,
    EntryLoader,
  },
  mixins: [CheckRef],
  data() {
    return {
      terms: false,
      consentName: false,
      mode: 'upload',
      cropSrc: null,
      messageType: null,
      exportingVideo: false,
      redBullLogo,
      showTerms: false,
      submissionBusy: false,
      cropperReady: false,
      thumbnailError: false,
      hideVideoSubmission: false,
      copyHeadline: CONTENT_GENERAL_HEADLINE,
      copyCallout: CONTENT_ENTRY_FORM_INSTRUCTIONS_CALLOUT,
      copyInstructions: CONTENT_ENTRY_FORM_INSTRUCTIONS_COPY,
      copyTerms: CONTENT_ENTRY_FORM_TERMS_COPY,
      copyTermsCta: CONTENT_ENTRY_FORM_TERMS_CTA,
      copyConsentName: CONTENT_ENTRY_FORM_CONSENT_NAME_COPY,
      copyResetCta: CONTENT_ENTRY_FORM_RESET_CTA,
      copyVideoThumbnails: CONTENT_ENTRY_FORM_THUMBNAILS_COPY,
      copyInstructionsCrop: CONTENT_ENTRY_FORM_INSTRUCTIONS_CROP,

      videoEntry: false, // true if the current upload is a video
      imageEntry: false, // true if the current upload is an image
      thumbnail: null, // thumbnail for entry
      entrySrc: null, // url of the current upload
      entryFile: null, // blob of the current upload
      entryMeta: {}, // info about the current upload we don't need for display, only for submission
      quicktime: false, // true if a video is quicktime
    };
  },

  beforeDestroy() {
    this.clearUrls();
  },

  computed: {
    /**
     * If the user can submit
     *
     * @returns {Boolean}
     */
    canSubmit() {
      return this.terms && this.entryFile;
    },

    /**
     * Text that appears on the continue button
     */
    copySubmit() {
      if (this.showTerms) {
        return CONTENT_ENTRY_FORM_CLOSE_TERMS_CTA;
      }
      return this.canSubmit
        ? CONTENT_ENTRY_FORM_SUBMIT_CTA
        : CONTENT_ENTRY_FORM_CONTINUE_CTA;
    },

    /**
     * Use the message component
     *
     * @returns {Boolean}
     */
    useMessage() {
      return !!this.messageType;
    },

    /**
     * Show the upload component
     *
     * @returns {Boolean}
     */
    showUpload() {
      return this.mode === 'upload';
    },

    /**
     * Show the frame UI
     *
     * @returns {Boolean}
     */
    showFrame() {
      return (
        this.useMessage
          || this.showUpload
          || this.showFile
      );
    },

    /**
     * Show the cropper section
     */
    showCropper() {
      return this.useCropper && !this.showTerms;
    },

    /**
     * Whether to show the loader
     */
    showLoader() {
      return (this.mode === 'crop' && !this.cropperReady) || this.submissionBusy;
    },
    /**
     * Use the video component
     *
     * @returns {Boolean}
     */
    useVideo() {
      return this.entrySrc && this.videoEntry && !this.quicktime;
    },

    /**
     * Show the video component
     *
     * @returns {Boolean}
     */
    showVideo() {
      return !this.useMessage && this.mode === 'video';
    },

    /**
     * Use the cropper component
     *
     * @returns {Boolean}
     */
    useCropper() {
      return !this.useMessage && this.mode === 'crop' && this.cropSrc;
    },

    /**
     * Submission category
     *
     * @returns {String}
     */
    entryCategory() {
      if (!this.entryFile) {
        return null;
      }
      return this.videoEntry
        ? SUBMISSION_CATEGORIES.video
        : SUBMISSION_CATEGORIES.image;
    },

    quicktimeMessage() {
      return this.useMessage && this.messageType === 'quicktime';
    },

    ...mapState('user', ['loggedIn'])
  },
  watch: {
    loggedIn: {
      immediate: true,

      /**
       * If the user is logged out, we need to get them off of the submission page ASAP.
       *
       * @param {Boolean} value false if the user is not logged in
       */
      handler(value) {
        if (!value) {
          this.$router.push({ name: ROUTE_NAMES.home });
        }
      }
    },
  },
  methods: {
    /**
     * Update the current mode
     */
    updateMode(crop) {
      if (!this.entrySrc) {
        this.mode = 'upload';
        return;
      }

      if (this.videoEntry && this.quicktime) {
        this.messageType = 'quicktime';
        this.submissionBusy = false;
        return;
      }

      if (crop) {
        this.mode = 'crop';
        return;
      }

      if (this.imageEntry) {
        this.mode = 'crop';
        return;
      }


      if (this.videoEntry) {
        this.mode = 'video';
      }
    },

    /**
     * User has selected a file to upload, we're actually doing all of this work in their browser's memory
     *
     * @param {*} data file data
     */
    handleUploadChange(data) {
      this.submissionBusy = true;
      ({
        video: this.videoEntry,
        image: this.imageEntry,
        url: this.entrySrc,
        file: this.entryFile,
        name: this.entryMeta.name,
        mimeType: this.entryMeta.mimeType,
        duration: this.entryMeta.duration,
        aspectRatio: this.entryMeta.aspectRatio
      } = data);

      this.quicktime = data.videoType === 'quicktime';

      console.log(data);

      if (this.imageEntry) {
        this.setCropSrc(this.entrySrc);
      }

      this.updateMode(true);
    },

    /**
     * Handle upload rotation
     *
     * @param imageData image data from cropper.js
     */
    handleUploadRotate({
      naturalHeight,
      naturalWidth,
      rotate
    }) {
      if ((Math.abs(rotate) === 90) && this.imageEntry) {
        // invert aspect ratio for images
        console.log('rotating', naturalHeight, naturalWidth, this.entryMeta.aspectRatio);
        this.entryMeta.aspectRatio = `${naturalHeight}:${naturalWidth}`;
      }
    },

    /**
     * Handle a click on the form message
     */
    handleMessageClick() {
      if (this.quicktime) {
        // submit if possible
        this.handleSubmitClick();
        return;
      }

      // clear error display
      this.handleReset(true);
    },

    /**
     * Reset the entry form
     *
     * @param {Boolean} error clear error as well
     */
    handleReset(error) {
      this.videoEntry = false;
      this.imageEntry = false;
      this.entrySrc = null;
      this.entryFile = null;
      this.cropSrc = null;
      this.thumbnailError = false;

      if (error) {
        this.messageType = null;
      }

      this.clearUrls();
      this.updateMode();
    },

    /**
     * Set the image src for cropper
     *
     * @param {String} src url
     */
    setCropSrc(value) {
      this.cropSrc = value;
    },

    /**
     * Revoke object urls
     */
    clearUrls() {
      URL.revokeObjectURL(this.entrySrc);
      URL.revokeObjectURL(this.cropSrc);
    },

    /**
     * When the thumbnail component has all the thumbnails, start setting them in crop mode
     *
     * @param {Array.<String>} thumbnail urls
     */
    handleExportThumbnails(thumbnails) {
      this.setCropSrc(thumbnails[0]);
      this.updateMode(true);
    },

    /**
     * Handle error from thumbnail generator
     */
    handleThumbnailError(error) {
      this.thumbnailError = error;
      this.handleError(error);
    },

    /**
     * When you click on back, go back to choosing thumbnail
     */
    handleBackClick() {
      this.updateMode(false);
    },

    handleTermsClick() {
      this.showTerms = true;
    },

    /**
     * set if the cropper is ready
     *
     * @param {Boolean} true if ready
     */
    handleCropperReady(ready) {
      if (ready) {
        this.handleUploadRotate(ready);
        this.cropperReady = true;
        return;
      }
      this.cropperReady = false;
    },

    handleSubmitClick() {
      if (this.disabled) {
        return;
      }
      this.hideVideoSubmission = true;
      if (this.showTerms) {
        this.showTerms = false;
        return;
      }

      this.doSubmission();
    },
    /**
     * Choose cropped thumbnail
     */
    cropImage() {
      if (this.cropping || !this.cropSrc) {
        return Promise.reject();
      }
      this.cropping = true;

      const cropper = this.checkRef('imageCropper');

      return cropper.exportBlob().then((blob) => {
        this.cropping = false;
        return blob;
      });
    },

    /**
     * Do the sumission.
     */
    async doSubmission() {
      if (!this.terms) {
        // user must accept terms
        // this.handleError(new Error(errors.UPLOAD_TERMS));
        return;
      }
      this.submissionBusy = true;

      const video = this.videoEntry && this.entryFile;
      const image = this.imageEntry && this.entryFile;

      // let image;

      // if (!video) {
      const thumbnail = (this.thumbnailError || this.quicktime) ? null : await this.cropImage();

      const submission = {
        ...this.entryMeta,
        image,
        video,
        thumbnail,
        category: this.entryCategory,
        consentName: this.consentName
      };

      this.makeSubmission({
        ...submission
      })
        .then(() => {
          // go to the success page
          console.info('successful!');

          this.$router.push({
            name: 'entrySuccess'
          });
        })
        .catch((error) => {
          if (error.message === errors.SUBMISSION_LIMIT) {
            console.info('failed because you already were successful!');

            this.$router.push({
              name: 'entrySuccess'
            });
            return;
          }
          this.submissionBusy = false;
          console.error(error);
          this.handleError(error);
        });
    },

    /**
     * Handle errors
     */
    handleError(error) {
      this.messageType = error.message;
      this.handleReset();
      this.error(error);
    },

    /**
     * Debug error for video canvas.
     *
     * @param {...*} logContent whatever you want to log
     */
    error: devError.bind(this, 'Entry Form:', true),

    ...mapActions({
      makeSubmission: SUBMISSION_MAKE_SUBMISSION
    })
  }
};
</script>

<style lang="scss">
.entry-form-view {
  &__headline {
    font-size: 44px;
    margin: 20px auto;
    padding: 0 2rem;
  }
  &__terms-display {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: $z-index-content;
  }

  &__preview-area {
    height: $size-height-entry-preview + (2 * $size-border-entry-preview);
    position: relative;

    &__frame {
      position: relative;
      overflow: hidden;
      height: $size-height-entry-preview;
      width: $size-width-entry-preview;
      @include media(small) {
        width: 95%;
      }
      margin: 0 auto;
      border: $size-border-entry-preview solid
        invert($color-border-entry-preview);
      border-radius: $size-border-radius-entry-preview;
    }
    &__image-cropper-wrap {
      display: flex;
      justify-content: center;
      height: 100%;
      width: 100%;
      opacity: 0;
      transition: opacity $time-global-transition-base linear;
      @include media(small) {
        flex-wrap: wrap;
        flex-direction: row;
        height: auto;
      }
      &--visible {
        opacity: 1;
      }
    }
    &__flex-wrap {
      flex: 0 1 auto;
      width: calc(100% - #{$size-width-entry-form-thumbnails-large});
      @include media(small) {
        width: 100%;
        max-height: 400px;
      }
    }

    &__loader {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: $color-background-page-entry;
      display: flex;
      .entry-loader {
        flex: 1 1 auto;
      }
    }
  }

  &__thumbnails {
    flex: 0 1 auto;
    width: $size-width-entry-form-thumbnails-large;
    min-width: $size-width-entry-form-thumbnails-large;
    margin-left: $size-margin-left-entry-form-thumbnails-large;
    display: flex;
    flex-direction: column;
    @include global-media(medium) {
      margin-left: $size-margin-left-entry-form-thumbnails-medium;
    }
    @include global-media(small) {
      width: 100%;
      margin-left: 0;
      flex-direction: column;
      text-align: left;
    }
  }

  &__thumbnails-label {
    font-family: $font-family-entry-view-thumbnails;
    line-height: $size-line-height-base;
    margin: 0;
    @include media(small) {
      margin-top: 2rem;
    }
  }
  &__instructions {
    margin-top: $size-margin-instructions;
    padding-top: 0.8rem;
    font-size: 1.3rem;

    @include media(small) {
      padding: 0;
    }
    &--callout {
      line-height: $size-line-height-base;
      font-weight: 500;
      font-size: 1.8rem;
      margin: 0;
    }
    &--copy {
      font-size: 1.8rem;
    }
    &--copy,
    &--terms {
      font-family: $font-family-limit-terms;
      line-height: $size-line-height-base;
      margin: 0;
    }
    &--terms {
      padding-top: 1rem;
    }
    &--terms, &--consent {
      text-align: left;
      @include media(small) {
        margin-left: 0;
      }
    }
    &--consent {
      padding-bottom: 1rem;
    }
    &__label {
      display: inline-block;
    }
  }
  &__submit {
    margin: $size-margin-button auto;
  }
  &__image-cropper {
    overflow: hidden;
  }
}
</style>
